import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Monitor, Users, BarChart3, Phone, ShieldCheck, Settings, ArrowRight, ExternalLink } from "lucide-react";

const portfolioItems = [
  {
    id: 1,
    title: "CRM Dashboard",
    category: "CRM System",
    description: "Advanced lead management with real-time analytics and automated workflows.",
    icon: Users,
    mockup: "crm",
  },
  {
    id: 2,
    title: "HRMS Portal",
    category: "HR Management",
    description: "Complete employee lifecycle management with payroll integration.",
    icon: Settings,
    mockup: "hrms",
  },
  {
    id: 3,
    title: "DNC Scrubber",
    category: "Compliance Tool",
    description: "Automated DNC list scrubbing with compliance reporting.",
    icon: ShieldCheck,
    mockup: "dnc",
  },
  {
    id: 4,
    title: "Analytics Panel",
    category: "Business Intelligence",
    description: "Real-time KPI tracking and performance dashboards.",
    icon: BarChart3,
    mockup: "analytics",
  },
  {
    id: 5,
    title: "Call Center Portal",
    category: "Operations",
    description: "Agent management and call routing optimization.",
    icon: Phone,
    mockup: "callcenter",
  },
  {
    id: 6,
    title: "Attendance System",
    category: "Workforce Management",
    description: "Biometric integration with shift scheduling.",
    icon: Monitor,
    mockup: "attendance",
  },
];

const MockDashboard = ({ type, isHovered }) => {
  const mockData = {
    crm: {
      stats: ["1,234 Leads", "85% Conversion", "$45K Revenue"],
      color: "blue",
      chart: [40, 65, 45, 80, 55, 70, 90],
    },
    hrms: {
      stats: ["156 Employees", "12 Departments", "98% Retention"],
      color: "purple",
      chart: [30, 50, 40, 60, 55, 75, 65],
    },
    dnc: {
      stats: ["50K Numbers", "99.9% Accuracy", "0 Violations"],
      color: "green",
      chart: [85, 90, 88, 95, 92, 98, 99],
    },
    analytics: {
      stats: ["24 Reports", "Real-time", "15 Dashboards"],
      color: "cyan",
      chart: [25, 45, 35, 55, 60, 50, 75],
    },
    callcenter: {
      stats: ["45 Agents", "2.3s Avg Wait", "4.8 Rating"],
      color: "orange",
      chart: [50, 40, 60, 45, 70, 55, 65],
    },
    attendance: {
      stats: ["156 Staff", "98% Present", "On Time: 95%"],
      color: "pink",
      chart: [90, 95, 88, 96, 92, 97, 94],
    },
  };

  const data = mockData[type] || mockData.crm;
  const colorMap = {
    blue: { bg: "bg-blue-500", text: "text-blue-500", light: "bg-blue-500/20" },
    purple: { bg: "bg-purple-500", text: "text-purple-500", light: "bg-purple-500/20" },
    green: { bg: "bg-green-500", text: "text-green-500", light: "bg-green-500/20" },
    cyan: { bg: "bg-cyan-500", text: "text-cyan-500", light: "bg-cyan-500/20" },
    orange: { bg: "bg-orange-500", text: "text-orange-500", light: "bg-orange-500/20" },
    pink: { bg: "bg-pink-500", text: "text-pink-500", light: "bg-pink-500/20" },
  };

  return (
    <div className="mock-dashboard h-44 relative overflow-hidden">
      <div className="mock-dashboard-header">
        <div className="mock-dot bg-red-500" />
        <div className="mock-dot bg-yellow-500" />
        <div className="mock-dot bg-green-500" />
        <span className="text-xs text-slate-400 ml-2">Dashboard</span>
      </div>
      <div className="p-4">
        {/* Stats row */}
        <div className="flex gap-2 mb-3">
          {data.stats.map((stat, i) => (
            <motion.div
              key={i}
              className="flex-1 bg-slate-800/50 rounded-lg p-2 text-center"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
            >
              <div className={`text-xs font-medium ${colorMap[data.color].text}`}>
                {stat.split(' ')[0]}
              </div>
              <div className="text-[10px] text-slate-500">
                {stat.split(' ').slice(1).join(' ')}
              </div>
            </motion.div>
          ))}
        </div>
        
        {/* Animated chart bars */}
        <div className="flex items-end gap-1 h-12">
          {data.chart.map((height, i) => (
            <motion.div
              key={i}
              className={`flex-1 ${colorMap[data.color].light} rounded-t`}
              initial={{ height: 0 }}
              animate={{ height: isHovered ? `${height}%` : `${height * 0.6}%` }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
            />
          ))}
        </div>
      </div>
      
      {/* Animated scan line */}
      <motion.div
        className="absolute left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500/50 to-transparent"
        animate={{ top: ['0%', '100%', '0%'] }}
        transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
      />
    </div>
  );
};

const PortfolioCard = ({ item, index, isInView }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: 0.1 * index }}
      whileHover={{ y: -10 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="glass-card overflow-hidden group cursor-pointer relative"
      data-testid={`portfolio-item-${index}`}
    >
      {/* Mock dashboard preview */}
      <div className="relative">
        <MockDashboard type={item.mockup} isHovered={isHovered} />
        
        {/* Gradient overlay */}
        <motion.div
          className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent"
          animate={{ opacity: isHovered ? 0.95 : 0.7 }}
          transition={{ duration: 0.3 }}
        />
        
        {/* Hover overlay with icon */}
        <AnimatePresence>
          {isHovered && (
            <motion.div
              className="absolute inset-0 flex items-center justify-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <motion.div
                className="w-12 h-12 bg-blue-500/20 backdrop-blur-sm rounded-full flex items-center justify-center border border-blue-500/30"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <ExternalLink className="text-blue-400" size={20} />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Content */}
      <div className="p-6 relative z-10 -mt-8">
        <motion.div 
          className="flex items-center gap-3 mb-3"
          animate={{ x: isHovered ? 5 : 0 }}
          transition={{ duration: 0.3 }}
        >
          <motion.div 
            className="w-10 h-10 bg-blue-500/20 rounded-xl flex items-center justify-center"
            whileHover={{ rotate: 360 }}
            transition={{ duration: 0.5 }}
          >
            <item.icon className="text-blue-400" size={20} />
          </motion.div>
          <span className="text-xs text-blue-400 uppercase tracking-wider font-medium">
            {item.category}
          </span>
        </motion.div>
        
        <motion.h3 
          className="text-xl font-semibold text-white mb-2 group-hover:text-blue-400 transition-colors"
          animate={{ x: isHovered ? 5 : 0 }}
        >
          {item.title}
        </motion.h3>
        <p className="text-sm text-slate-400">
          {item.description}
        </p>
        
        {/* View project link */}
        <motion.div
          className="mt-4 flex items-center gap-2 text-blue-400"
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: isHovered ? 1 : 0, x: isHovered ? 0 : -10 }}
          transition={{ duration: 0.3 }}
        >
          <span className="text-sm font-medium">View Project</span>
          <motion.div
            animate={{ x: isHovered ? [0, 5, 0] : 0 }}
            transition={{ duration: 0.8, repeat: Infinity }}
          >
            <ArrowRight size={14} />
          </motion.div>
        </motion.div>
      </div>
      
      {/* Animated border on hover */}
      <motion.div
        className="absolute inset-0 pointer-events-none rounded-2xl"
        style={{
          background: 'linear-gradient(90deg, transparent, transparent)',
          padding: 1,
        }}
        animate={{
          background: isHovered
            ? 'linear-gradient(90deg, rgba(59,130,246,0.5), rgba(147,51,234,0.5), rgba(59,130,246,0.5))'
            : 'linear-gradient(90deg, transparent, transparent)',
        }}
        transition={{ duration: 0.3 }}
      />
    </motion.div>
  );
};

const Portfolio = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <section
      id="portfolio"
      ref={ref}
      className="relative py-24 md:py-32 bg-slate-950/50 overflow-hidden"
      data-testid="portfolio-section"
    >
      {/* Animated background elements */}
      <motion.div
        className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl"
        animate={{ 
          x: [0, 50, 0],
          y: [0, 30, 0],
        }}
        transition={{ duration: 15, repeat: Infinity }}
      />
      <motion.div
        className="absolute bottom-0 right-1/4 w-80 h-80 bg-purple-500/5 rounded-full blur-3xl"
        animate={{ 
          x: [0, -30, 0],
          y: [0, -50, 0],
        }}
        transition={{ duration: 12, repeat: Infinity }}
      />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <motion.span 
            className="text-sm uppercase tracking-widest text-blue-400 font-medium mb-4 block"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2 }}
          >
            Our Portfolio
          </motion.span>
          <motion.h2 
            className="text-3xl md:text-5xl font-bold tracking-tight mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.3 }}
          >
            Solutions <span className="text-gradient">Showcase</span>
          </motion.h2>
          <motion.p 
            className="text-lg text-slate-400 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.4 }}
          >
            Explore some of the powerful software systems we've built for call centers 
            and businesses worldwide.
          </motion.p>
        </motion.div>

        {/* Portfolio grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {portfolioItems.map((item, index) => (
            <PortfolioCard
              key={item.id}
              item={item}
              index={index}
              isInView={isInView}
            />
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="text-center mt-16"
        >
          <motion.p 
            className="text-slate-400 mb-6"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ delay: 0.9 }}
          >
            Want to see more? Let's discuss how we can build something amazing for your business.
          </motion.p>
          <motion.a
            href="#contact"
            onClick={(e) => {
              e.preventDefault();
              document.querySelector("#contact").scrollIntoView({ behavior: "smooth" });
            }}
            whileHover={{ scale: 1.05, borderColor: "rgba(59, 130, 246, 0.5)" }}
            whileTap={{ scale: 0.95 }}
            className="inline-flex items-center justify-center gap-2 bg-transparent border border-slate-700 text-white hover:bg-slate-800/50 rounded-full px-8 py-4 font-medium transition-all duration-300 group"
            data-testid="portfolio-cta"
          >
            Start Your Project
            <motion.div
              animate={{ x: [0, 5, 0] }}
              transition={{ duration: 1, repeat: Infinity }}
            >
              <ArrowRight size={18} className="group-hover:text-blue-400 transition-colors" />
            </motion.div>
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

export default Portfolio;
